import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useCreatePart } from "../services/PartService";
import type { PartGroup } from "../services/PartGroupService";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";

interface CreatePartModalProps {
  open: boolean;
  onClose: () => void;
  partGroups: PartGroup[];
}

const CreatePartModal = ({
  open,
  onClose,
  partGroups,
}: CreatePartModalProps) => {
  const [name, setName] = useState("");
  const [partType, setPartType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [description, setDescription] = useState("");
  const [partGroupFk, setPartGroupFk] = useState<number | "">("");

  const createPart = useCreatePart();

  const handleSubmit = () => {
    if (!name.trim()) return;
    createPart.mutate(
      {
        name: name.trim(),
        part_type: partType.trim() || null,
        manufacturer: manufacturer.trim() || null,
        description: description.trim() || null,
        part_group_fk: partGroupFk === "" ? null : partGroupFk,
      },
      {
        onSuccess: () => {
          handleReset();
          onClose();
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error("Failed to create part", err);
          NucToast({
            message: `Failed to create part: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
        },
      },
    );
  };

  const handleReset = () => {
    setName("");
    setPartType("");
    setManufacturer("");
    setDescription("");
    setPartGroupFk("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={createPart.isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Part</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Part Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Part Type"
            value={partType}
            onChange={(e) => setPartType(e.target.value)}
            fullWidth
            placeholder="e.g., Valve, Flange, Pump"
          />
          <TextField
            label="Manufacturer"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Part Group</InputLabel>
            <Select
              value={partGroupFk}
              label="Part Group"
              onChange={(e) => {
                const raw = e.target.value;
                setPartGroupFk(raw === "" ? "" : Number(raw));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {partGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createPart.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || createPart.isLoading}
          startIcon={
            createPart.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {createPart.isLoading ? "Creating..." : "Create Part"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePartModal;
